import { useState } from "react";
import { formatMoney, type CurrencyFormat } from "../../domain/money/format";
import type { Money } from "../../domain/money/money";
import {
  rateFromPercent,
  promoDiscountTotal,
  DiscountError,
  type DiscountableLine,
} from "../../domain/pricing/promo-discount";

/**
 * A percentage off the order (FR-032).
 *
 * The everyday case is a round number for someone the owner knows, so the
 * presets carry it in one tap and the keypad is there for the rest. What the
 * customer comes off is shown before anything is applied — a cashier should
 * never have to approve a figure they have not seen.
 *
 * Manager approval is required and is requested by the caller, so this
 * component stays a form and the same approval dialog serves discounts, voids,
 * refunds and cash variances. Approval is what makes the discount attributable:
 * `discount_application.authorized_by` is the record of who allowed it.
 */

const PRESETS = [10, 15, 20, 50];

export interface DiscountDialogProps {
  lines: readonly DiscountableLine[];
  currency: CurrencyFormat;
  /** The rate already in force, if the cashier is changing it. */
  currentRateBp: number | null;
  onApply(rateBp: number): void;
  onRemove(): void;
  onCancel(): void;
  /**
   * A Senior Citizen / PWD discount is the OTHER thing this one slot can
   * hold (order-discounts.ts, FR-032) — reachable from here rather than a
   * second button on the cart, so the till's button row never grows just
   * because a second discount TYPE exists.
   */
  onSwitchToStatutory(): void;
}

export function DiscountDialog({
  lines,
  currency,
  currentRateBp,
  onApply,
  onRemove,
  onCancel,
  onSwitchToStatutory,
}: DiscountDialogProps) {
  const [percent, setPercent] = useState("");
  const [error, setError] = useState<string | null>(null);

  const typed = percent === "" ? null : Number(percent);

  /** What this rate takes off, or null when the rate is not usable yet. */
  const preview = (value: number | null): Money | null => {
    if (value === null) return null;
    try {
      return promoDiscountTotal(lines, rateFromPercent(value));
    } catch {
      return null;
    }
  };

  const typedPreview = preview(typed);

  const apply = (value: number | null) => {
    if (value === null) return;
    try {
      onApply(rateFromPercent(value));
    } catch (caught) {
      setError(caught instanceof DiscountError ? caught.message : "That discount is not valid.");
    }
  };

  return (
    <div className="discount" role="dialog" aria-label="Discount the order">
      <h2 className="discount__title">Discount the order</h2>

      <div className="discount__presets">
        {PRESETS.map((value) => {
          const off = preview(value);
          return (
            <button
              key={value}
              type="button"
              className={`discount__preset${rateFromPercent(value) === currentRateBp ? " is-selected" : ""}`}
              onClick={() => apply(value)}
            >
              <span className="discount__preset-rate">{value}%</span>
              {off !== null && (
                <span className="discount__preset-off">−{formatMoney(off, currency)}</span>
              )}
            </button>
          );
        })}
      </div>

      <label className="discount__custom">
        <span className="discount__custom-label">Another amount</span>
        <span className="discount__custom-field">
          <input
            className="discount__input"
            inputMode="decimal"
            placeholder="0"
            value={percent}
            onChange={(event) => {
              setPercent(event.target.value);
              setError(null);
            }}
            aria-label="Discount percentage"
          />
          <span className="discount__percent">%</span>
        </span>
      </label>

      {typedPreview !== null && (
        <p className="discount__preview">
          Takes off <span className="money">{formatMoney(typedPreview, currency)}</span>
        </p>
      )}

      {error && (
        <p className="discount__error" role="alert">
          {error}
        </p>
      )}

      <p className="discount__note">A manager has to approve this.</p>

      <button type="button" className="discount__switch" onClick={onSwitchToStatutory}>
        Senior Citizen / PWD discount instead
      </button>

      <div className="discount__actions">
        <button type="button" onClick={onCancel}>
          Cancel
        </button>

        {currentRateBp !== null && (
          <button type="button" className="discount__remove" onClick={onRemove}>
            Remove discount
          </button>
        )}

        <button
          type="button"
          className="is-primary"
          disabled={typedPreview === null}
          onClick={() => apply(typed)}
        >
          Apply
        </button>
      </div>
    </div>
  );
}

export default DiscountDialog;
